import React, { useMemo, useState } from 'react';
import { ChevronDown, Check, Minus, Search, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { normalizeText } from '../../../../../utils/text';

/**
 * Tri-state checkbox rendered from React state instead of the native
 * `indeterminate` DOM property, so no direct DOM access is needed.
 *
 * @param {object} props
 * @param {boolean} props.checked
 * @param {boolean} [props.indeterminate]
 */
const CheckboxIndicator = ({ checked, indeterminate }) => (
  <span
    className={clsx(
      'w-[18px] h-[18px] rounded-md border flex items-center justify-center transition-all shrink-0',
      checked || indeterminate
        ? 'bg-ninja-green border-ninja-green text-white'
        : 'border-border bg-white dark:bg-[#1a1a1a]'
    )}
  >
    {indeterminate ? (
      <Minus size={12} strokeWidth={3} />
    ) : checked ? (
      <Check size={12} strokeWidth={3} />
    ) : null}
  </span>
);

/**
 * @typedef {object} NestedCommune
 * @property {number} id
 * @property {string} name
 * @property {string} [system_name]
 */

/**
 * @typedef {object} Region
 * @property {number} id
 * @property {string} name
 * @property {Array<NestedCommune>} communes
 */

/**
 * Hierarchical, collapsible commune picker grouped by region.
 *
 * Each region can be toggled as a whole; the region checkbox reflects a partial
 * selection as an indeterminate state. The search box filters the tree in place
 * and auto-expands the regions that still hold matches.
 *
 * @param {object} props
 * @param {Array<Region>} [props.regions=[]]
 * @param {Array<number>} [props.selectedCommuneIds=[]]
 * @param {(communeIds: Array<number>) => void} props.onChange
 * @param {boolean} [props.loading=false]
 */
export const RegionCommuneSelector = ({
  regions = [],
  selectedCommuneIds = [],
  onChange,
  loading = false,
}) => {
  const [openRegions, setOpenRegions] = useState({});
  const [search, setSearch] = useState('');

  // Normalized once and reused across every commune, instead of per candidate.
  const normalizedSearch = normalizeText(search.trim());

  const filteredRegions = useMemo(() => {
    if (!normalizedSearch) return regions;

    return regions
      .map((region) => {
        const regionMatches = normalizeText(region.name).includes(normalizedSearch);
        const communes = (region.communes || []).filter((commune) =>
          normalizeText(commune.name).includes(normalizedSearch)
        );

        // A region kept by its own name shows its full list of communes.
        if (regionMatches) return region;
        if (communes.length > 0) return { ...region, communes };
        return null;
      })
      .filter(Boolean);
  }, [regions, normalizedSearch]);

  const handleToggleRegionView = (regionId) => {
    setOpenRegions((previous) => ({ ...previous, [regionId]: !previous[regionId] }));
  };

  const handleToggleCommune = (communeId) => {
    if (selectedCommuneIds.includes(communeId)) {
      onChange(selectedCommuneIds.filter((id) => id !== communeId));
      return;
    }
    onChange([...selectedCommuneIds, communeId]);
  };

  /**
   * Selects or clears every commune of a region at once, honouring the current
   * search so a filtered view only acts on what is visible.
   *
   * @param {Region} region
   */
  const handleToggleRegionSelection = (region) => {
    const communeIds = (region.communes || []).map((commune) => commune.id);
    const isAllSelected = communeIds.every((id) => selectedCommuneIds.includes(id));

    if (isAllSelected) {
      onChange(selectedCommuneIds.filter((id) => !communeIds.includes(id)));
      return;
    }

    const missing = communeIds.filter((id) => !selectedCommuneIds.includes(id));
    onChange([...selectedCommuneIds, ...missing]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-3 py-10 border border-border rounded-xl bg-ninja-gray/40 dark:bg-[#111]">
        <Loader2 size={18} className="text-ninja-green animate-spin" />
        <span className="text-sm text-text-secondary">Cargando estructura geográfica...</span>
      </div>
    );
  }

  return (
    <div className="border border-border rounded-xl overflow-hidden bg-white dark:bg-[#1a1a1a]">
      <div className="p-3 border-b border-border bg-ninja-gray/40 dark:bg-[#111]">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={16} className="text-text-muted" />
          </div>
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Filtrar regiones o comunas..."
            className="w-full pl-9 pr-4 py-2 bg-white dark:bg-[#1a1a1a] border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ninja-green focus:border-transparent transition-all dark:text-white"
          />
        </div>
      </div>

      <div className="max-h-[320px] overflow-y-auto divide-y divide-border">
        {filteredRegions.length === 0 ? (
          <p className="py-8 text-center text-sm text-text-secondary">
            No se encontraron regiones ni comunas para esa búsqueda.
          </p>
        ) : (
          filteredRegions.map((region) => {
            const communes = region.communes || [];
            const communeIds = communes.map((commune) => commune.id);
            const selectedCount = communeIds.filter((id) =>
              selectedCommuneIds.includes(id)
            ).length;

            const isAllSelected = communeIds.length > 0 && selectedCount === communeIds.length;
            const isIndeterminate = selectedCount > 0 && !isAllSelected;
            // While searching, keep matching regions open so hits stay visible.
            const isOpen = Boolean(openRegions[region.id]) || Boolean(normalizedSearch);

            return (
              <div key={region.id}>
                <div
                  className={clsx(
                    'flex items-center gap-2 px-3 py-2.5 transition-colors',
                    isOpen ? 'bg-ninja-gray/60 dark:bg-[#222]' : 'hover:bg-ninja-gray/40 dark:hover:bg-[#222]'
                  )}
                >
                  <button
                    type="button"
                    onClick={() => handleToggleRegionSelection(region)}
                    className="p-1 -m-1 rounded-md focus:outline-none focus:ring-2 focus:ring-ninja-green"
                    title={isAllSelected ? 'Quitar toda la región' : 'Seleccionar toda la región'}
                  >
                    <CheckboxIndicator checked={isAllSelected} indeterminate={isIndeterminate} />
                  </button>

                  <button
                    type="button"
                    onClick={() => handleToggleRegionView(region.id)}
                    className="flex-1 flex items-center justify-between gap-3 text-left"
                  >
                    <span className="flex flex-col">
                      <span className="text-sm font-semibold text-ninja-dark dark:text-white">
                        {region.name}
                      </span>
                      <span className="text-xs text-text-muted">
                        {selectedCount} / {communeIds.length} seleccionadas
                      </span>
                    </span>
                    <ChevronDown
                      size={16}
                      className={clsx(
                        'text-text-muted transition-transform duration-200',
                        isOpen && 'rotate-180'
                      )}
                    />
                  </button>
                </div>

                {isOpen && (
                  <div className="pb-2 pl-8 pr-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                    {communes.map((commune) => {
                      const isSelected = selectedCommuneIds.includes(commune.id);
                      return (
                        <button
                          key={commune.id}
                          type="button"
                          onClick={() => handleToggleCommune(commune.id)}
                          className="flex items-center gap-2 py-1.5 text-left rounded-lg hover:bg-ninja-gray/60 dark:hover:bg-[#222] px-2 transition-colors"
                        >
                          <CheckboxIndicator checked={isSelected} />
                          <span
                            className={clsx(
                              'text-sm',
                              isSelected
                                ? 'text-ninja-dark dark:text-white font-medium'
                                : 'text-text-secondary'
                            )}
                          >
                            {commune.name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
